import { localeFor } from './languages';

/**
 * Looking a UI string up, and never showing a shopper or merchant a key.
 *
 * Pure: no React, no module state beyond a warn-once set. The provider hands
 * this the dictionaries it has loaded and the order to try languages in, and
 * gets back the `t` every component calls.
 *
 * Key grammar - `t('nav.products')`:
 *   the first segment is the namespace (the dictionary file,
 *   `dictionaries/<lang>/nav.ts`) and the rest is the key inside it
 *   (`products`). Keys inside a namespace may contain dots themselves
 *   (`t('common.errors.network')` reads key `errors.network` of `common`).
 *
 * Interpolation - `{name}` placeholders are filled from `vars`. A placeholder
 * nobody supplied is left standing, so a typo shows up in review instead of
 * becoming a sentence with a hole in it. Values are inserted as-is: format
 * money, dates and big numbers with `format.ts` before passing them.
 *
 * Plurals - passing a numeric `vars.count` switches to plural lookup, using
 * CLDR categories from `Intl.PluralRules` for the language the text is found
 * in. For `t('cart.items', { count: n })` the dictionary may define:
 *   `items_zero`   (optional, used for exactly 0 in every language)
 *   `items_one`, `items_two`, `items_few`, `items_many` (as the language needs)
 *   `items_other`  (required whenever any plural form exists)
 *   `items`        (plain key, last resort)
 * English only needs `_one` and `_other`.
 *
 * Fallback - each language in `fallbackChain` is tried in order (the provider
 * passes `[active, 'en']`). A key missing everywhere renders its last segment
 * humanised (`errors.not_found` -> "Not found") and warns once in dev.
 *
 * Variants - a store's industry can reword a string: a clinic's "Products"
 * reads "Our services". The provider passes the variants to try, most
 * specific first (`['clinics', 'sector_services']`), and within each language
 * the `industryTerms` namespace is searched for `<variant>:<full key>`
 * (`clinics:nav.pages.catalog.label`) before the key itself. Language wins
 * over variant: a Hindi clinic with no Hindi override sees the plain Hindi
 * word, never an English one, so a screen never mixes languages.
 */

export type Dictionary = Readonly<Record<string, string>>;

/** One language's dictionaries, by namespace. */
export type NamespaceDictionaries = Readonly<Record<string, Dictionary>>;

/** Every loaded language's dictionaries, by language code. */
export type DictionarySet = Readonly<Record<string, NamespaceDictionaries>>;

export type TranslateVars = Readonly<Record<string, string | number | null | undefined>>;

export type Translator = (key: string, vars?: TranslateVars) => string;

/** The namespace holding industry rewordings, keyed `<variant>:<namespace>.<key>`. */
export const VARIANTS_NAMESPACE = 'industryTerms';

const warned = new Set<string>();

function warnMissing(key: string): void {
  if (!import.meta.env.DEV || warned.has(key)) return;
  warned.add(key);
  console.warn(`[i18n] Missing translation for "${key}"`);
}

/** `not_found` -> "Not found", `tooLong` -> "Too long", `products` -> "Products". */
export function humaniseKey(key: string): string {
  const last = key.split('.').filter(Boolean).pop() ?? key;
  const words = last
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  if (!words) return key || '…';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Fill `{name}` placeholders; unknown ones stay visible. */
export function interpolate(text: string, vars?: TranslateVars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined || value === null ? whole : String(value);
  });
}

const pluralRules = new Map<string, Intl.PluralRules>();

function pluralCategory(locale: string, count: number): Intl.LDMLPluralRule {
  let rules = pluralRules.get(locale);
  if (!rules) {
    try {
      rules = new Intl.PluralRules(locale);
    } catch {
      rules = new Intl.PluralRules('en');
    }
    pluralRules.set(locale, rules);
  }
  return rules.select(count);
}

function splitKey(key: string): [namespace: string, rest: string] {
  const dot = key.indexOf('.');
  return dot === -1 ? ['', key] : [key.slice(0, dot), key.slice(dot + 1)];
}

function countOf(vars?: TranslateVars): number | null {
  const raw = vars?.count;
  if (raw === undefined || raw === null || raw === '') return null;
  const count = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(count) ? count : null;
}

function usable(value: string | undefined): value is string {
  return typeof value === 'string' && value !== '';
}

/** `rest` (or its plural form for `count`) from one dictionary, filled in. */
function lookup(
  dictionary: Dictionary | undefined,
  rest: string,
  count: number | null,
  language: string,
  vars?: TranslateVars,
): string | undefined {
  if (!dictionary) return undefined;
  if (count !== null) {
    const candidates = [
      ...(count === 0 ? [`${rest}_zero`] : []),
      `${rest}_${pluralCategory(localeFor(language), count)}`,
      `${rest}_other`,
    ];
    const plural = candidates.map((candidate) => dictionary[candidate]).find(usable);
    if (plural) return interpolate(plural, vars);
  }
  const value = dictionary[rest];
  return usable(value) ? interpolate(value, vars) : undefined;
}

/**
 * A `t` over these dictionaries, trying languages in `fallbackChain` order
 * and, within each, the `variants` (see "Variants" above) before the plain key.
 *
 * Duplicate and empty entries in the chain are ignored, so `[lang, 'en']` is
 * safe to pass when `lang` is already English.
 */
export function createTranslator(
  dictionaries: DictionarySet,
  fallbackChain: readonly string[],
  variants: readonly string[] = [],
): Translator {
  const chain = [...new Set(fallbackChain.filter(Boolean))];
  const variantChain = [...new Set(variants.filter(Boolean))];

  return (key, vars) => {
    const [namespace, rest] = splitKey(key);
    const count = countOf(vars);

    for (const language of chain) {
      const terms = variantChain.length ? dictionaries[language]?.[VARIANTS_NAMESPACE] : undefined;
      for (const variant of terms ? variantChain : []) {
        const found = lookup(terms, `${variant}:${key}`, count, language, vars);
        if (found !== undefined) return found;
      }

      const found = lookup(dictionaries[language]?.[namespace], rest, count, language, vars);
      if (found !== undefined) return found;
    }

    warnMissing(key);
    return interpolate(humaniseKey(rest), vars);
  };
}
